import React, { useEffect, useRef, useState, useCallback } from 'react';
import { SlidersHorizontal, Settings, Compass, Plus, Star, Search, Download, ChevronDown, FileSpreadsheet, House } from 'lucide-react';
import { useWarehouseStore } from '../../store/warehouseStore';
import { useProjectStore } from '../../store/projectStore';
import StockDashboard from './StockDashboard';
import { useStockDashboardStore } from '../../store/stockDashboardStore';
import { useStockUiStore } from '../../store/stockUiStore';
import { useStockRequestStore } from '../../store/stockRequestStore';
import PreferencesDrawer from './PreferencesDrawer';
import StockPreferencesDrawer from './StockPreferencesDrawer';
import InventoryView from './InventoryView';
import ExcelImportModal from './ExcelImportModal';
import StockMovementView from './StockMovementView';
import RequestsView from './requests/RequestsView';
import StockLogView from './StockLogView';
import { useInventoryStore } from '../../store/inventoryStore';
import { useOwnersStore } from '../../store/ownersStore';
import { downloadInventoryExport } from '../../utils/inventoryExcel';

const InventoryIcon: React.FC<{ size?: number }> = ({ size = 48 }) => (
  <span
    className="material-symbols-outlined text-gray-400"
    style={{ fontSize: size, lineHeight: 1, display: 'inline-flex' }}
  >
    inventory_2
  </span>
);

const WhTooltip: React.FC<{ label: string; isDefault: boolean; onClick?: () => void }> = ({ label, isDefault, onClick }) => {
  const [visible, setVisible] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative flex items-center gap-1 text-sm text-gray-500 font-normal rounded px-1 -mx-1 transition-colors hover:text-gray-300 hover:bg-gray-700/40"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <House size={13} className="flex-shrink-0 text-gray-400" strokeWidth={2.5} style={{ marginTop: 2 }} />
      <span className="truncate max-w-[160px]">{label}</span>
      {isDefault && <Star size={11} className="flex-shrink-0 fill-yellow-400 text-yellow-400" />}
      {visible && (
        <span className="absolute left-0 top-full mt-2 z-50 pointer-events-none flex flex-col gap-0.5 whitespace-nowrap rounded-md px-3 py-2 text-xs shadow-xl"
          style={{ backgroundColor: '#1e2330', border: '1px solid #343a43' }}>
          <span className="font-medium text-white">{label}</span>
          <span className="text-gray-400">{onClick ? 'Click to edit warehouses' : 'Showing inventory for this warehouse'}</span>
        </span>
      )}
    </button>
  );
};

const WarehouseManagerView: React.FC = () => {
  const { warehouses, addWarehouse, activeWarehouseId, defaultWarehouseId, storageAreasByWarehouse, setActiveWarehouse } = useWarehouseStore();
  const { appTheme } = useProjectStore();
  const isDarkTheme = appTheme === 'dark';
  const [isWarehousesOpen, setIsWarehousesOpen] = useState(false);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [isScopeOpen, setIsScopeOpen] = useState(false);
  const { activeStockTab, setActiveStockTab, inventorySubView, setInventorySubView } = useStockUiStore();
  const fetchRequests = useStockRequestStore(s => s.fetchRequests);
  const [inventorySearch, setInventorySearch] = useState('');
  const [isCreateItemMenuOpen, setIsCreateItemMenuOpen] = useState(false);
  const [isExcelImportOpen, setIsExcelImportOpen] = useState(false);
  const [isInventoryDropdownOpen, setIsInventoryDropdownOpen] = useState(false);
  const addInventoryItemRef = useRef<(() => void) | null>(null);
  const createItemMenuRef = useRef<HTMLDivElement | null>(null);
  const inventoryDropdownRef = useRef<HTMLDivElement | null>(null);
  const handleAddItemRef = useCallback((fn: () => void) => { addInventoryItemRef.current = fn; }, []);
  const handleExportInventory = useCallback(() => {
    const { items, stockUnits } = useInventoryStore.getState();
    downloadInventoryExport(items, stockUnits);
  }, []);
  const scopePopoverRef = useRef<HTMLDivElement | null>(null);
  const {
    isEditMode,
    enterEditMode,
    exitEditMode,
    scopeMode,
    globalScopeWarehouseId,
    globalScopeStorageAreaId,
    setScopeMode,
    setGlobalScope,
  } = useStockDashboardStore();

  const stockTabs = ['Dashboard', 'Inventory', 'Stock Movement', 'Requests', 'Reports', 'Log'];

  const hasWarehouses = warehouses.length > 0;

  // Track previous defaultWarehouseId so we can detect when the user changes it
  const prevDefaultWarehouseId = useRef<string | null>(defaultWarehouseId);
  const warehousesReady = useRef(false);

  useEffect(() => {
    if (activeStockTab === 'Dashboard') fetchRequests();
  }, [activeStockTab, fetchRequests]);

  useEffect(() => {
    if (warehouses.length === 0) return;
    const warehouseIds = new Set(warehouses.map(w => w.id));
    const fallbackId =
      (defaultWarehouseId && warehouseIds.has(defaultWarehouseId) ? defaultWarehouseId : null)
      ?? (activeWarehouseId && warehouseIds.has(activeWarehouseId) ? activeWarehouseId : null)
      ?? warehouses[0].id;

    const syncToDefault = (id: string) => {
      setGlobalScope(id, null);
      if (activeWarehouseId !== id) setActiveWarehouse(id);
    };

    // First load — prefer DB default over stale persisted scope
    if (!warehousesReady.current) {
      warehousesReady.current = true;
      if (defaultWarehouseId && warehouseIds.has(defaultWarehouseId)) {
        if (globalScopeWarehouseId !== defaultWarehouseId || activeWarehouseId !== defaultWarehouseId) {
          syncToDefault(defaultWarehouseId);
        }
      }
      prevDefaultWarehouseId.current = defaultWarehouseId;
      return;
    }

    // User changed the default warehouse — follow it immediately
    if (
      defaultWarehouseId &&
      defaultWarehouseId !== prevDefaultWarehouseId.current &&
      warehouseIds.has(defaultWarehouseId)
    ) {
      prevDefaultWarehouseId.current = defaultWarehouseId;
      syncToDefault(defaultWarehouseId);
      return;
    }
    prevDefaultWarehouseId.current = defaultWarehouseId;

    // Missing scope — seed from default / active warehouse
    if (!globalScopeWarehouseId) {
      setGlobalScope(fallbackId, null);
      return;
    }

    // Stale scope from localStorage (e.g. warehouse deleted or DB reset) — realign to a valid warehouse
    if (!warehouseIds.has(globalScopeWarehouseId)) {
      setGlobalScope(fallbackId, null);
      return;
    }

    // Storage area must belong to the scoped warehouse
    if (globalScopeStorageAreaId) {
      const areas = storageAreasByWarehouse[globalScopeWarehouseId] ?? [];
      if (!areas.some(a => a.id === globalScopeStorageAreaId)) {
        setGlobalScope(globalScopeWarehouseId, null);
      }
    }
  }, [
    globalScopeWarehouseId,
    globalScopeStorageAreaId,
    defaultWarehouseId,
    activeWarehouseId,
    warehouses,
    storageAreasByWarehouse,
    setGlobalScope,
    setActiveWarehouse,
  ]);

  useEffect(() => {
    const onDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (isScopeOpen && scopePopoverRef.current && !scopePopoverRef.current.contains(target)) {
        setIsScopeOpen(false);
      }
      if (isCreateItemMenuOpen && createItemMenuRef.current && !createItemMenuRef.current.contains(target)) {
        setIsCreateItemMenuOpen(false);
      }
      if (isInventoryDropdownOpen && inventoryDropdownRef.current && !inventoryDropdownRef.current.contains(target)) {
        setIsInventoryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [isScopeOpen, isCreateItemMenuOpen, isInventoryDropdownOpen]);


  if (!hasWarehouses) {
    return (
      <>
        <div className="flex-1 flex items-center justify-center bg-gray-900">
          <div className="text-center">
            <div className="mb-4 flex justify-center">
              <InventoryIcon />
            </div>
            <h3 className="text-lg font-medium text-gray-200 mb-2">
              No Warehouses Yet
            </h3>
            <p className="text-gray-400 mb-4">
              Create your first warehouse to start managing stock.
            </p>
            <button
              onClick={() => setIsWarehousesOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <Plus size={16} />
              Add Warehouse
            </button>
          </div>
        </div>
        {isWarehousesOpen && (
          <PreferencesDrawer onClose={() => setIsWarehousesOpen(false)} />
        )}
      </>
    );
  }

  return (
    <div className={`flex-1 flex flex-col overflow-hidden relative ${isDarkTheme ? 'bg-[#171a1f]' : 'bg-gray-900'}`}>
      {/* Header Section - mirrors Site Manager styling */}
      <div className={`${isDarkTheme ? 'bg-[#20242b] border-b border-[#343a43]' : 'bg-gray-800 border-b border-gray-700'} px-4 pt-2 pb-0`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-white">Stock Control</h1>
            {(() => {
              const activeWh = warehouses.find(w => w.id === defaultWarehouseId) ?? warehouses.find(w => w.id === activeWarehouseId) ?? warehouses[0];
              if (!activeWh) return null;
              const isDefault = defaultWarehouseId === activeWh.id;
              return (
                <>
                  <span className="text-gray-600 text-base select-none">·</span>
                  <WhTooltip
                    label={activeWh.name}
                    isDefault={isDefault}
                    onClick={() => {
                      setIsPreferencesOpen(false);
                      setIsWarehousesOpen(true);
                    }}
                  />
                </>
              );
            })()}
          </div>
          <div className="flex items-center gap-2">
            {activeStockTab === 'Inventory' && (
              <div className="relative">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                <input
                  type="text"
                  value={inventorySearch}
                  onChange={e => setInventorySearch(e.target.value)}
                  placeholder="Search item / ID / serial…"
                  className="h-7 text-xs pl-7 pr-3 rounded border bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 w-52"
                />
              </div>
            )}
            {activeStockTab === 'Inventory' && inventorySubView === 'item_types' && (
              <>
                {/* Create New Item dropdown */}
                <div className="relative" ref={createItemMenuRef}>
                  <button
                    onClick={() => setIsCreateItemMenuOpen(prev => !prev)}
                    className="inline-flex items-center gap-1.5 h-7 px-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors text-xs font-medium"
                  >
                    <Plus size={13} />
                    Create New Item
                    <ChevronDown size={11} className={`transition-transform ${isCreateItemMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isCreateItemMenuOpen && (
                    <div className="absolute right-0 mt-1 w-40 rounded-md shadow-xl z-30 overflow-hidden"
                      style={{ backgroundColor: '#1e2330', border: '1px solid #343a43' }}>
                      <button
                        onClick={() => { setIsCreateItemMenuOpen(false); addInventoryItemRef.current?.(); }}
                        className="w-full text-left px-3 py-2 text-xs text-gray-200 hover:bg-gray-700 transition-colors flex items-center gap-2"
                      >
                        <Plus size={12} className="text-gray-400" />
                        Manual
                      </button>
                      <button
                        onClick={() => { setIsCreateItemMenuOpen(false); setIsExcelImportOpen(true); }}
                        className="w-full text-left px-3 py-2 text-xs text-gray-200 hover:bg-gray-700 transition-colors flex items-center gap-2"
                      >
                        <FileSpreadsheet size={12} className="text-green-400" />
                        Import Excel
                      </button>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleExportInventory}
                  className="inline-flex items-center gap-1.5 h-7 px-2.5 bg-gray-700 text-gray-200 rounded hover:bg-gray-600 transition-colors text-xs font-medium"
                >
                  <Download size={13} />
                  Export
                </button>
              </>
            )}

            {/* Edit Widgets — only relevant on the Dashboard tab */}
            {activeStockTab === 'Dashboard' && (
              <>
                {isEditMode ? (
                  <button
                    onClick={() => exitEditMode(true)}
                    className="inline-flex items-center gap-1.5 h-7 px-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors text-xs font-medium"
                  >
                    <Settings size={13} />
                    Done Editing
                  </button>
                ) : (
                  <button
                    onClick={enterEditMode}
                    className="inline-flex items-center gap-1.5 h-7 px-2.5 bg-gray-700 text-gray-200 rounded hover:bg-gray-600 transition-colors text-xs font-medium"
                  >
                    <Settings size={13} />
                    Edit Widgets
                  </button>
                )}

                <div className="relative" ref={scopePopoverRef}>
                  <button
                    onClick={() => setIsScopeOpen(prev => !prev)}
                    className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded transition-colors text-xs font-medium ${
                      isScopeOpen ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    }`}
                    title="Widget Scope"
                  >
                    <Compass size={13} />
                    Widget Scope
                  </button>
                  {isScopeOpen && (
                    <div className="absolute right-0 mt-1 w-64 bg-gray-800 border border-gray-700 rounded-md shadow-xl z-30 p-3 space-y-2">
                      <div className="text-[11px] uppercase tracking-wide text-gray-400 pb-1">Scope mode</div>
                      <label className="flex items-center gap-2 text-xs text-gray-200 cursor-pointer">
                        <input
                          type="radio"
                          checked={scopeMode === 'global'}
                          onChange={() => setScopeMode('global')}
                        />
                        <span>
                          Default Warehouse
                          {(() => {
                            const dw = warehouses.find(w => w.id === defaultWarehouseId) ?? warehouses[0];
                            return dw ? <span className="text-gray-400 ml-1">({dw.name})</span> : null;
                          })()}
                        </span>
                      </label>
                      <label className="flex items-center gap-2 text-xs text-gray-200 cursor-pointer">
                        <input
                          type="radio"
                          checked={scopeMode === 'per_widget'}
                          onChange={() => setScopeMode('per_widget')}
                        />
                        Per-widget Scope
                      </label>
                    </div>
                  )}
                </div>
              </>
            )}
            <button
              onClick={() => {
                setIsPreferencesOpen(false);
                setIsWarehousesOpen(prev => !prev);
              }}
              className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded transition-colors text-xs font-medium ${
                isWarehousesOpen ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
              }`}
            >
              <SlidersHorizontal size={14} />
              Edit Warehouses
            </button>
            <button
              onClick={() => {
                setIsWarehousesOpen(false);
                setIsPreferencesOpen(prev => !prev);
              }}
              className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded transition-colors text-xs font-medium ${
                isPreferencesOpen ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
              }`}
            >
              <Settings size={14} />
              Preferences
            </button>
            {isWarehousesOpen && (
              <PreferencesDrawer onClose={() => setIsWarehousesOpen(false)} />
            )}
            {isPreferencesOpen && (
              <StockPreferencesDrawer onClose={() => setIsPreferencesOpen(false)} />
            )}
          </div>
        </div>
        <div className="flex items-end gap-1 flex-wrap translate-y-px relative z-20">
          {stockTabs.map((tab) => {
            const isActive = activeStockTab === tab;
            const activeCls = isDarkTheme ? 'bg-[#171a1f] text-white' : 'bg-white text-gray-900';
            const inactiveCls = isDarkTheme ? 'bg-[#2b3140] text-gray-200 hover:bg-[#353c4c]' : 'bg-gray-200 text-gray-700 hover:bg-gray-300';
            const baseCls = `rounded-t-md rounded-b-none transition-colors text-sm font-medium ${isActive ? activeCls : inactiveCls}`;
            const dividerColor = isDarkTheme ? (isActive ? '#343a43' : '#3a4155') : (isActive ? '#d1d5db' : '#b0b8c8');

            if (tab === 'Inventory') {
              const subLabel = inventorySubView === 'item_types' ? 'Item Types' : 'Inventory';
              return (
                <div key={tab} className="relative flex items-end" ref={inventoryDropdownRef}>
                  <div className={`flex items-stretch overflow-hidden rounded-t-md rounded-b-none ${isActive ? activeCls : inactiveCls}`}>
                    {/* Left: label */}
                    <button
                      type="button"
                      onClick={() => setActiveStockTab('Inventory')}
                      className="px-3 py-1.5 text-sm font-medium transition-colors"
                    >
                      {subLabel}
                    </button>
                    {/* Divider */}
                    <span className="self-stretch w-px my-1.5 flex-shrink-0" style={{ backgroundColor: dividerColor }} />
                    {/* Right: chevron */}
                    <button
                      type="button"
                      onClick={() => { setActiveStockTab('Inventory'); setIsInventoryDropdownOpen(prev => !prev); }}
                      className="px-1.5 py-1.5 transition-colors"
                    >
                      <ChevronDown size={12} style={{ transform: isInventoryDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 150ms' }} />
                    </button>
                  </div>
                  {isInventoryDropdownOpen && (
                    <div
                      className="absolute top-full left-0 mt-1 z-[100] rounded-md shadow-xl overflow-hidden min-w-[130px]"
                      style={{ backgroundColor: isDarkTheme ? '#1e2330' : '#ffffff', border: `1px solid ${isDarkTheme ? '#343a43' : '#e5e7eb'}` }}
                    >
                      {([
                        { key: 'stock',      label: 'Inventory' },
                        { key: 'item_types', label: 'Item Types' },
                      ] as const).map(opt => (
                        <button
                          key={opt.key}
                          onClick={() => { setInventorySubView(opt.key); setIsInventoryDropdownOpen(false); setActiveStockTab('Inventory'); }}
                          className="w-full text-left px-3 py-2 text-xs transition-colors flex items-center gap-2"
                          style={{
                            color: inventorySubView === opt.key ? '#60a5fa' : (isDarkTheme ? '#e5e7eb' : '#111827'),
                            backgroundColor: inventorySubView === opt.key ? (isDarkTheme ? '#1d2535' : '#eff6ff') : 'transparent',
                          }}
                          onMouseEnter={e => { if (inventorySubView !== opt.key) (e.currentTarget as HTMLElement).style.backgroundColor = isDarkTheme ? '#2b3140' : '#f3f4f6'; }}
                          onMouseLeave={e => { if (inventorySubView !== opt.key) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveStockTab(tab)}
                className={`px-3 py-1.5 ${baseCls}`}
              >
                {tab}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content Area */}

      {/* Dashboard — always mounted to preserve grid state, hidden when another tab is active */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden" style={{ display: activeStockTab === 'Dashboard' ? 'flex' : 'none' }}>
        <StockDashboard />
      </div>

      {/* Inventory — always mounted to preserve filters / scroll state */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden" style={{ display: activeStockTab === 'Inventory' ? 'flex' : 'none' }}>
        <InventoryView
          searchQuery={inventorySearch}
          subView={inventorySubView}
          onAddItemRef={handleAddItemRef}
        />
      </div>

      {/* Stock Movement — always mounted to preserve sub-tab + in-progress forms (e.g. Transfer) */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden" style={{ display: activeStockTab === 'Stock Movement' ? 'flex' : 'none' }}>
        <StockMovementView />
      </div>

      {/* Requests — always mounted to preserve sub-tab / scroll state */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden" style={{ display: activeStockTab === 'Requests' ? 'flex' : 'none' }}>
        <RequestsView />
      </div>

      {/* Log */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden" style={{ display: activeStockTab === 'Log' ? 'flex' : 'none' }}>
        <StockLogView />
      </div>

      {/* Other tabs — placeholder */}
      {activeStockTab !== 'Dashboard' && activeStockTab !== 'Inventory' && activeStockTab !== 'Stock Movement' && activeStockTab !== 'Requests' && activeStockTab !== 'Log' && (
        <div className={`flex-1 flex items-center justify-center ${isDarkTheme ? 'bg-[#171a1f]' : 'bg-gray-100'}`}>
          <p className={`text-sm ${isDarkTheme ? 'text-gray-500' : 'text-gray-400'}`}>
            {activeStockTab} — coming soon
          </p>
        </div>
      )}

      {/* Excel import modal */}
      {isExcelImportOpen && (
        <ExcelImportModal
          onClose={() => setIsExcelImportOpen(false)}
          onDone={() => {
            const inv = useInventoryStore.getState();
            const wh = useWarehouseStore.getState();
            void inv.fetchItems();
            void inv.fetchStockUnits(wh.defaultWarehouseId ?? undefined);
            void useOwnersStore.getState().fetchOwners();
          }}
        />
      )}
    </div>
  );
};

export default WarehouseManagerView;
